import * as React from 'react';
import { PolygonEntityType } from '@/app/appContext';
import { EntityActionKey } from './entityActions';

/** State pushed by CanvasRenderer when it wants to take over the page-level
 *  toolbar slot (during polygon creation or editing). When state is null,
 *  the parent shows the default PolygonToolbar. */
export type ToolbarSlotState =
  | null
  | {
      kind: 'creation';
      entityType: PolygonEntityType;
      pointCount: number;
      awaitingRackSelection: boolean;
      rackSelected: boolean;
      onCancel: () => void;
      onSave: () => void;
      onExit: () => void;
    }
  | {
      kind: 'edit';
      activeType: PolygonEntityType | null;
      saving: boolean;
      onCancel: () => void;
      onSave: () => void;
      onAction: (key: EntityActionKey) => void;
      isAvailable: (key: EntityActionKey) => boolean;
    };

interface ToolbarSlotContextValue {
  state: ToolbarSlotState;
  setState: (next: ToolbarSlotState) => void;
}

const ToolbarSlotContext = React.createContext<ToolbarSlotContextValue>({
  state: null,
  setState: () => {},
});

export function ToolbarSlotProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ToolbarSlotState>(null);
  const value = React.useMemo(() => ({ state, setState }), [state]);
  return <ToolbarSlotContext.Provider value={value}>{children}</ToolbarSlotContext.Provider>;
}

export function useToolbarSlot(): ToolbarSlotContextValue {
  return React.useContext(ToolbarSlotContext);
}
